import { useContext, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import Box from 'components/ui/box';

import FeedbackImg from 'assets/img/comportamento/bg-feedback-moedas.png';

interface Props {
  callback: (obj: { [key: string]: any }) => void;
  data: any;
  activeEvaluationStep: number;
}

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen || !ref.current) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // ESC fecha a modal
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      // Sempre previne o comportamento padrão do TAB
      event.preventDefault();

      const focusableElements = Array.from(
        ref.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ) as HTMLElement[];

      if (focusableElements.length === 0) return;

      const activeElement = document.activeElement as HTMLElement;
      let currentIndex = focusableElements.indexOf(activeElement);

      // Se não encontrou o elemento atual na lista, começa do início
      if (currentIndex === -1) {
        currentIndex = event.shiftKey ? 0 : -1;
      }

      let nextIndex: number;
      if (event.shiftKey) {
        // SHIFT+TAB: vai para o anterior (ou último se estiver no primeiro)
        nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
      } else {
        // TAB: vai para o próximo (ou primeiro se estiver no último)
        nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
      }

      focusableElements[nextIndex].focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, ref, onClose]);
};

export default function ModalReset(props: Props) {
  ReactModal.setAppElement('#modal');

  const modalID = 'validation-reset';

  const { openModal, closeModal } = useModal();
  const { callback, data, activeEvaluationStep } = props;

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  function handleCloseModal() {
    closeModal();
  }

  useFocusTrap(modalContentRef, modalIsOpen, handleCloseModal);

  function afterOpenModal() {
    if (confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }

  useEffect(() => {
    if (typeof openModal === 'string' && openModal === `${modalID}-${data._id}`) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [data._id, openModal]);

  if (!modalIsOpen) return <></>;

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Desfazer ação ECOA"
        aria-modal="true"
        aria-labelledby="reset-modal-title"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div ref={modalContentRef}>
            <Box className={`w-[90%] lg:w-auto lg:max-w-[600px]`}>
              <Button
                color="primary"
                className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                aria-label="Fechar"
                onClick={() => closeModal()}
              >
                <IoIosClose color="white" size={40} />
              </Button>

              <div className="w-full lg:w-[536px] flex flex-col items-center justify-center space-y-8">
                <h4 id="reset-modal-title" className="block text-center text-yellow text-lg font-semibold leading-none">
                  Deseja mesmo desfazer sua {activeEvaluationStep <= 2 ? 'validação' : 'apreciação'}?
                </h4>
                <p className="text-alice-blue text-base font-thin block">
                  Ao confirmar, a Ação ECOA voltará ao estado inicial de seu nível de apreciação.
                </p>
                <button
                  ref={confirmBtnRef}
                  className={`text-white bg-slate-blue text-sm inline-flex items-center justify-center font-semibold uppercase rounded py-3 px-4 leading-none`}
                  onClick={() => {
                    callback({ id: 'reset', task: data });
                    closeModal();
                  }}
                >
                  CONFIRMAR
                </button>
              </div>
            </Box>
          </div>
        )}
      />
    </>
  );
}
